import React, { useState, useEffect, useRef } from "react";
import { X, UserCircle, ChevronLeft, Loader2 } from "lucide-react";
import { Portal } from "./ui/Portal";
import { getEmployees } from "../services/employeesApi";
import { Employee } from "../types/employee";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../hooks/useToast";
import { Button } from "./ui/Button";
import { PinKeypad } from "./ui/PinKeypad";

interface EmployeeLoginDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EmployeeLoginDrawer: React.FC<EmployeeLoginDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const { impersonate } = useAuth();
  const toast = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"list" | "pin">("list");
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pinInputRef = useRef<HTMLInputElement>(null);

  // Allow keyboard entry even when focus is on keypad (so you can mix typing + keypad)
  useEffect(() => {
    if (step !== "pin" || !selectedEmployee) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as Node;
      if (pinInputRef.current && pinInputRef.current.contains(target)) return;
      if (e.key >= "0" && e.key <= "9") {
        setPin((prev) => (prev + e.key).replace(/\D/g, "").slice(0, 8));
        e.preventDefault();
      } else if (e.key === "Backspace") {
        setPin((prev) => prev.slice(0, -1));
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [step, selectedEmployee]);

  useEffect(() => {
    if (isOpen) {
      setStep("list");
      setSelectedEmployee(null);
      setPin("");
      setLoading(true);
      getEmployees()
        .then((data) => {
          setEmployees(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          toast.error("Failed to load employees");
          setEmployees([]);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const handleSelectEmployee = (emp: Employee) => {
    setSelectedEmployee(emp);
    setStep("pin");
    setPin("");
  };

  const handleBack = () => {
    setStep("list");
    setSelectedEmployee(null);
    setPin("");
  };

  const handleSubmitPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee || submitting) return;
    setSubmitting(true);
    try {
      await impersonate(selectedEmployee.id, pin);
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? "Login failed. Check PIN.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep("list");
    setSelectedEmployee(null);
    setPin("");
    onClose();
  };

  return (
    <Portal>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[2999] transition-opacity duration-300"
          onClick={handleClose}
        />
      )}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-[3000] transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {isOpen && (
          <button
            aria-label="Close drawer"
            className="fixed -left-14 top-9 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center text-[#885ABB] hover:bg-purple-50 transition z-[3001]"
            onClick={handleClose}
          >
            <X strokeWidth={3} />
          </button>
        )}

        <div className="px-6 py-6 border-b border-gray-100">
          {step === "pin" ? (
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-2 text-sm text-[#885ABB] hover:text-[#7748A4] mb-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          ) : null}
          <h2 className="text-xl font-semibold text-[#402B69]">
            {step === "list" ? "Employee login" : "Enter PIN"}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {step === "list"
              ? "Select an employee to log in as"
              : `Logging in as ${selectedEmployee?.name ?? ""}`}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === "list" && (
            <>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-10 h-10 text-[#885ABB] animate-spin" />
                  <p className="text-sm text-gray-500 mt-3">Loading employees...</p>
                </div>
              ) : employees.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No employees found.</p>
              ) : (
                <ul className="space-y-2">
                  {employees
                    .filter((emp) => (emp.role || "").toLowerCase() === "employee")
                    .map((emp) => (
                      <li key={emp.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectEmployee(emp)}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-200 hover:border-[#885ABB] hover:bg-purple-50/50 transition text-left"
                        >
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                            {emp.image || emp.user_image_url ? (
                              <img
                                src={emp.image || emp.user_image_url || ""}
                                alt={emp.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <UserCircle className="w-6 h-6 text-gray-400" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-medium text-gray-900 truncate">{emp.name}</p>
                              {emp.pin_protected && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
                                  PIN protected
                                </span>
                              )}
                            </div>
                            {emp.email ? (
                              <p className="text-xs text-gray-500 truncate">{emp.email}</p>
                            ) : null}
                          </div>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </>
          )}

          {step === "pin" && selectedEmployee && (
            <form onSubmit={handleSubmitPin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PIN code
                </label>
                <input
                  ref={pinInputRef}
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  placeholder="Enter PIN"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  className="w-full px-4 py-4 rounded-2xl border-2 border-[#885ABB]/30 bg-gray-50/50 text-center text-lg tracking-[0.4em] min-h-[56px] focus:outline-none focus:ring-2 focus:ring-[#885ABB]/50 focus:border-[#885ABB]"
                  maxLength={8}
                  aria-label="PIN code"
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1.5 text-center">
                  Type with keyboard or use the keypad below — you can mix both
                </p>
              </div>
              <PinKeypad
                value={pin}
                onChange={(v) => setPin(v.replace(/\D/g, "").slice(0, 8))}
                maxLength={8}
                pinOnly
              />
              <Button
                type="submit"
                disabled={!pin.trim() || submitting}
                className="w-full bg-[#885ABB] hover:bg-[#7748A4] text-white py-3 font-medium"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Logging in...
                  </>
                ) : (
                  "Log in as employee"
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default EmployeeLoginDrawer;
